import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/shared/i18n/useT';
import * as api from './queries';
import { TEMPLATES, editorPathFor } from './templates';
import type { FoldersDocType } from './types';
import { useFoldersErrors } from './useFoldersErrors';

interface Options {
  readonly orgId: string | null;
  /** The folder the new thing lands in. `null` is the root. */
  readonly parentId: string | null;
  readonly userId: string | null;
  readonly readOnly?: boolean;
  /** Called after a successful write so the caller can refresh its listing. */
  readonly onCreated?: () => void;
}

export interface NodeCreate {
  readonly createFolder: () => void;
  readonly createDocument: (docType: FoldersDocType) => void;
  readonly busy: boolean;
  /** Already translated, including the plan-limit sentences with real numbers. */
  readonly errorMessage: string | null;
  readonly disabled: boolean;
}

/**
 * The Create button's two actions, without the screen around them.
 *
 * Home and the Folders browser both offer Create over the same root folder, and
 * before this the logic was inlined in the browser — so a second Create button
 * meant a second copy of the plan-limit handling and the straight-into-the-editor
 * navigation. Both callers get one implementation instead.
 */
export function useNodeCreate({ orgId, parentId, userId, readOnly = false, onCreated }: Options): NodeCreate {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { message: errorMessage, report, clear } = useFoldersErrors();

  const run = useCallback(
    async (action: () => Promise<void>, fallbackKey: Parameters<typeof report>[1]) => {
      if (busy || readOnly || !orgId) return;
      setBusy(true);
      clear();
      try {
        await action();
        onCreated?.();
      } catch (cause) {
        report(cause, fallbackKey);
      } finally {
        setBusy(false);
      }
    },
    [busy, clear, onCreated, orgId, readOnly, report],
  );

  const createFolder = useCallback(() => {
    if (!orgId) return;
    void run(async () => {
      await api.createFolder(orgId, parentId, t('folders.newName.folder'), userId);
    }, 'folders.error.create');
  }, [orgId, parentId, run, t, userId]);

  const createDocument = useCallback(
    (docType: FoldersDocType) => {
      if (!orgId) return;
      const template = TEMPLATES[docType];
      void run(async () => {
        const node = await api.createDocument(
          orgId,
          parentId,
          docType,
          template.defaultName(t),
          template.defaultTabName(t),
          template.columns(t),
          template.initialRows,
          userId,
          template.additionalTabs?.(t),
        );
        // Straight into the editor: creating a file and then having to find it
        // is the wrong end of the interaction.
        navigate(editorPathFor(node.id, node.docType));
      }, 'folders.error.create');
    },
    [navigate, orgId, parentId, run, t, userId],
  );

  return { createFolder, createDocument, busy, errorMessage, disabled: busy || readOnly || !orgId };
}
